var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
// Material ui Imports
import { styled } from "@mui/system";
import { Box, Card, CardActionArea, CardActions, CardContent, CardMedia, Grid, IconButton, Skeleton, Tooltip, Typography, alpha, } from "@mui/material";
import VTButton from "./VTButton";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useOnWindowResize } from "../hooks/useOnWindowResize";
import VTCanvas from "./VTCanvas";
import VTCube from "../components/VTCube";
import * as THREE from "three";
import ThreeSixtyRoundedIcon from "@mui/icons-material/ThreeSixtyRounded";
import VTTextField from "./VTTextField";
import VTMenu from "./VTMenu";
import VTSphere from "./VTSphere";
import textCutOffAddElipsis from "../utils/textCutOffAddElipsis";
import SaveAsRoundedIcon from "@mui/icons-material/SaveAsRounded";
import DoneOutlineRoundedIcon from "@mui/icons-material/DoneOutlineRounded";
import VTCardModelPreview from "./VTCardModelPreview";
//---------------------------------------------------------------------------------------------------
// ## MUI STYLED ##
// StyledCard used to display the back card
var StyledCard = styled(Card)(function () { return ({
    borderRadius: 15,
    maxWidth: "400px",
}); });
function getMediaTypeAsString(mediaType) {
    if (mediaType.hasOwnProperty("textures")) {
        return "ImageCardMedia360";
    }
    else if (mediaType.hasOwnProperty("videoUrl")) {
        return "VideoCardMedia360";
    }
    else if (mediaType.hasOwnProperty("modelUrl")) {
        return "ModelCardMedia";
    }
    else {
        return "ImageCardMedia";
    }
}
function CardImage(_a) {
    var _b = _a.mediaType, mediaType = _b === void 0 ? {} : _b, loading = _a.loading, video = _a.video, setVideo = _a.setVideo, cardMediaHeight = _a.cardMediaHeight, currentImageTextures = _a.currentImageTextures, cardMediaClicked = _a.cardMediaClicked, cardMedia360Load = _a.cardMedia360Load, model3dAutoSpin = _a.model3dAutoSpin;
    if (loading) {
        return _jsx(Skeleton, { variant: "rectangular", sx: { width: "100%", height: "100%" } });
    }
    if (getMediaTypeAsString(mediaType) === "ImageCardMedia") {
        return !mediaType.imageUrl ? (_jsx("img", { src: "/images/2d_image_default.png", style: {
                width: "auto",
                height: "".concat(cardMediaHeight - 85, "px"),
            }, alt: "card preview" })) : (_jsx("img", { src: mediaType.imageUrl, style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            }, alt: "card media" }));
    }
    if (getMediaTypeAsString(mediaType) === "ImageCardMedia360") {
        return mediaType.showPreview === false || cardMediaClicked ? (cardMedia360Load && currentImageTextures ? (_jsx(VTCanvas, __assign({ style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            } }, { children: _jsx(VTCube, { loading: cardMedia360Load }) }))) : (_jsx(VTCanvas, __assign({ style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            } }, { children: _jsx(VTCube, { image: currentImageTextures }) })))) : mediaType.preview ? (_jsx("img", { src: mediaType.preview, style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            }, alt: "card preview" })) : (_jsx("img", { src: "/images/360_image_default_image.png", style: {
                width: "auto",
                height: "".concat(cardMediaHeight - 125, "px"),
            }, alt: "card media" }));
    }
    if (getMediaTypeAsString(mediaType) === "VideoCardMedia360") {
        return mediaType.showPreview === false || cardMediaClicked ? (cardMedia360Load && mediaType.videoUrl ? (_jsx(VTCanvas, __assign({ style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            } }, { children: _jsx(VTCube, { loading: cardMedia360Load }) }))) : (_jsx(VTCanvas, __assign({ style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            } }, { children: _jsx(VTSphere, { videoSrc: mediaType.videoUrl, video: video, setVideo: setVideo }) })))) : mediaType.preview ? (_jsx("img", { src: mediaType.preview, style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            }, alt: "card media" })) : (_jsx("img", { src: "/images/360_video_default_image.png", style: {
                width: "auto",
                height: "".concat(cardMediaHeight - 125, "px"),
            }, alt: "card media" }));
    }
    if (getMediaTypeAsString(mediaType) === "ModelCardMedia") {
        return mediaType.showPreview === false || cardMediaClicked ? (cardMedia360Load && mediaType.modelUrl ? (_jsx(VTCanvas, __assign({ style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            } }, { children: _jsx(VTCube, { loading: cardMedia360Load }) }))) : (_jsx(VTCardModelPreview, { modelUrl: mediaType.modelUrl, autoSpin: model3dAutoSpin }))) : mediaType.preview ? (_jsx("img", { src: mediaType.preview, style: {
                objectFit: "cover",
                width: "100%",
                height: "100%",
            }, alt: "card media" })) : (_jsx("img", { src: "/images/3d_model_default_image.png", style: {
                width: "auto",
                height: "".concat(cardMediaHeight - 125, "px"),
            }, alt: "card media" }));
    }
    return _jsx(_Fragment, {});
}
// Card content for the visuals and layout of the VTCard
var CardContentComponentWrapper = forwardRef(function (props, ref) {
    var _a = props.mediaType, mediaType = _a === void 0 ? {} : _a, title = props.title, text = props.text, _b = props.size, size = _b === void 0 ? 100 : _b, primaryActionButton = props.primaryActionButton, secondaryActionButton = props.secondaryActionButton, sx = props.sx, _c = props.titleCutOffPoint, titleCutOffPoint = _c === void 0 ? 80 : _c, _d = props.textCutOffPoint, textCutOffPoint = _d === void 0 ? 80 : _d, onTitleEdited = props.onTitleEdited, menuOptions = props.menuOptions, loading = props.loading, scenarioIsComplete = props.scenarioIsComplete, cardIsHovered = props.cardIsHovered;
    var _e = __read(useOnWindowResize(), 2), windowWidth = _e[0], windowHeight = _e[1];
    var _f = __read(useState(null), 2), cardMediaNode = _f[0], setCardMediaNode = _f[1];
    var CardMediaRef = useCallback(function (node) {
        setCardMediaNode(node);
    }, []);
    var _g = __read(useState(false), 2), cardMediaClicked = _g[0], setCardMediaClicked = _g[1];
    var _h = __read(useState(false), 2), cardMedia360Load = _h[0], setCardMedia360Load = _h[1];
    var _j = __read(useState(false), 2), disableRippleEffect = _j[0], setDisableRippleEffect = _j[1];
    var _k = __read(useState(null), 2), currentImageTextures = _k[0], setCurrentImageTextures = _k[1];
    var cardMedia360HasLoaded = useRef(false);
    useEffect(function () {
        var tId = null;
        if ((mediaType.showPreview === false || cardMediaClicked) &&
            getMediaTypeAsString(mediaType) === "ImageCardMedia360" &&
            mediaType.textures) {
            if (!cardMedia360HasLoaded.current) {
                cardMedia360HasLoaded.current = true;
                setCardMedia360Load(true);
                tId = window.setTimeout(function () {
                    setCardMedia360Load(false);
                }, 1500);
                setCurrentImageTextures({});
                var textures = mediaType.textures;
                var loader = new THREE.TextureLoader();
                // @ts-ignore
                var backTexture = loader.load(textures.backTextureUrl);
                backTexture.encoding = THREE.sRGBEncoding;
                // @ts-ignore
                var frontTexture = loader.load(textures.frontTextureUrl);
                frontTexture.encoding = THREE.sRGBEncoding;
                // @ts-ignore
                var leftTexture = loader.load(textures.leftTextureUrl);
                leftTexture.encoding = THREE.sRGBEncoding;
                // @ts-ignore
                var rightTexture = loader.load(textures.rightTextureUrl);
                rightTexture.encoding = THREE.sRGBEncoding;
                // @ts-ignore
                var upTexture = loader.load(textures.upTextureUrl);
                upTexture.encoding = THREE.sRGBEncoding;
                // @ts-ignore
                var downTexture = loader.load(textures.downTextureUrl);
                downTexture.encoding = THREE.sRGBEncoding;
                setCurrentImageTextures({
                    left: leftTexture,
                    right: rightTexture,
                    up: upTexture,
                    down: downTexture,
                    front: frontTexture,
                    back: backTexture,
                });
            }
        }
        else if ((mediaType.showPreview === false || cardMediaClicked) &&
            getMediaTypeAsString(mediaType) === "VideoCardMedia360" &&
            mediaType.videoUrl) {
            if (!cardMedia360HasLoaded.current) {
                cardMedia360HasLoaded.current = true;
                setCardMedia360Load(true);
                tId = window.setTimeout(function () {
                    setCardMedia360Load(false);
                }, 1500);
            }
        }
        return function () {
            tId && window.clearTimeout(tId);
        };
    }, [cardMediaClicked, mediaType.showPreview]);
    var _l = __read(useState(150), 2), cardMediaHeight = _l[0], setCardMediaHeight = _l[1];
    useEffect(function () {
        if (cardMediaNode) {
            var width = cardMediaNode.clientWidth;
            if (185 > width) {
                setCardMediaHeight(150);
            }
            else if (250 > width) {
                setCardMediaHeight(200);
            }
            else if (350 > width) {
                setCardMediaHeight(240);
            }
            else {
                setCardMediaHeight(280);
            }
        }
    }, [cardMediaNode, sx, windowWidth]);
    var _m = __read(useState(document.createElement("video")), 2), video = _m[0], setVideo = _m[1];
    var mouseMovementDelta = 6;
    var startXMouseMovement = useRef(0);
    var startYMouseMovement = useRef(0);
    var _o = __read(useState(false), 2), titleInEdit = _o[0], setTitleInEdit = _o[1];
    var _p = __read(useState(""), 2), editedTitleValue = _p[0], setEditedTitleValue = _p[1];
    var _q = __read(useState(true), 2), modelAutoSpin = _q[0], setModelAutoSpin = _q[1];
    var _r = __read(useState(false), 2), load3dModel = _r[0], setLoad3dModel = _r[1];
    return (_jsxs(_Fragment, { children: [_jsx(CardActionArea, __assign({ disableRipple: !mediaType.showPreview ||
                    disableRippleEffect ||
                    !["ImageCardMedia360", "VideoCardMedia360", "ModelCardMedia"].includes(getMediaTypeAsString(mediaType)), sx: __assign({}, (!["ImageCardMedia360", "VideoCardMedia360", "ModelCardMedia"].includes(getMediaTypeAsString(mediaType)) &&
                    primaryActionButton && {
                    cursor: "default",
                    "& .MuiCardActionArea-focusHighlight": {
                        background: "transparent",
                    },
                })) }, { children: _jsxs(CardMedia, __assign({ onMouseDown: function (e) {
                        startXMouseMovement.current = e.pageX;
                        startYMouseMovement.current = e.pageY;
                        if (getMediaTypeAsString(mediaType) === "ModelCardMedia") {
                            if (cardMediaClicked || !mediaType.showPreview) {
                                setModelAutoSpin(false);
                            }
                        }
                    }, onMouseUp: function (e) {
                        var diffX = Math.abs(e.pageX - startXMouseMovement.current);
                        var diffY = Math.abs(e.pageY - startYMouseMovement.current);
                        if (diffX < mouseMovementDelta && diffY < mouseMovementDelta) {
                            // Click!
                            if (getMediaTypeAsString(mediaType) === "VideoCardMedia360") {
                                if (cardMediaClicked) {
                                    if (video.paused) {
                                        video.play();
                                    }
                                    else {
                                        video.pause();
                                    }
                                }
                            }
                        }
                    }, onClick: function () {
                        if (!cardMediaClicked && !titleInEdit) {
                            if (!cardMediaClicked && getMediaTypeAsString(mediaType) === "ModelCardMedia") {
                                // setTimeout(() => {
                                //     setLoad3dModel(false);
                                // }, 1500)
                            }
                            setCardMediaClicked(true);
                            ["ImageCardMedia360", "VideoCardMedia360", "ModelCardMedia"].includes(getMediaTypeAsString(mediaType)) &&
                                window.setTimeout(function () {
                                    setDisableRippleEffect(true);
                                }, 350);
                        }
                    }, ref: CardMediaRef, sx: {
                        height: "".concat(cardMediaHeight, "px"),
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                    } }, { children: [["ImageCardMedia360", "VideoCardMedia360", "ModelCardMedia"].includes(getMediaTypeAsString(mediaType)) && (_jsx(Box, __assign({ component: "div", sx: {
                                zIndex: 1,
                                height: "50px",
                                width: "50px",
                                position: "absolute",
                                borderRadius: "13px",
                                top: "10px",
                                left: "10px",
                                background: "rgb(0, 0, 0, 0.4)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#eee",
                                pointerEvents: "none",
                            } }, { children: _jsx(ThreeSixtyRoundedIcon, { sx: {
                                    width: "35px",
                                    height: "35px",
                                    opacity: "0.7",
                                } }) }))), ["ImageCardMedia360", "VideoCardMedia360", "ModelCardMedia"].includes(getMediaTypeAsString(mediaType)) && (_jsx(Box, __assign({ component: "div", sx: {
                                zIndex: 1,
                                display: "inline-block",
                                position: "absolute",
                                borderRadius: "13px",
                                padding: "5px 10px 7px 10px",
                                top: "10px",
                                left: "70px",
                                background: "rgb(0, 0, 0, 0.4)",
                                color: "#eee",
                                pointerEvents: "none",
                            } }, { children: _jsxs(Typography, __assign({ variant: "caption", sx: __assign({ lineHeight: 0.8, opacity: "0.8", fontSize: "0.82em", "@keyframes flash-360-preview-text": {
                                        "0%": {
                                            opacity: 0.7,
                                        },
                                        "50%": {
                                            opacity: 1,
                                        },
                                        "100%": {
                                            opacity: 0.7,
                                        },
                                    } }, (mediaType.showPreview &&
                                    !cardMediaClicked && cardIsHovered && {
                                    animation: "flash-360-preview-text 0.5s ease infinite",
                                })) }, { children: [mediaType.showPreview && !cardMediaClicked && (_jsxs("span", { children: [" ", "click to view ", _jsx("br", {}), " "] })), getMediaTypeAsString(mediaType) === "ImageCardMedia360" && "360 Image", getMediaTypeAsString(mediaType) === "VideoCardMedia360" && "360 Video", getMediaTypeAsString(mediaType) === "ModelCardMedia" && "3D Model"] })) }))), !["ImageCardMedia360", "VideoCardMedia360"].includes(getMediaTypeAsString(mediaType)) &&
                            scenarioIsComplete && (_jsxs(Box, __assign({ component: "div", sx: {
                                width: "55px",
                                height: "55px",
                                position: "absolute",
                                top: "-10px",
                                left: "-10px",
                                zIndex: 2,
                                background: alpha("#00cc66", 1),
                                // transform: "rotate(90deg)",
                                // opacity: "0.6",
                                borderRadius: "15px",
                            } }, { children: [" ", _jsx(DoneOutlineRoundedIcon, { sx: { position: "absolute", top: "20px", left: "20px" } })] }))), menuOptions && (_jsx(VTMenu, __assign({ ref: ref, sx: {
                                width: "45px",
                                height: "45px",
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                zIndex: 2,
                                "& .MuiButtonBase-root": {
                                    background: "rgb(0, 0, 0, 0.05)",
                                    "&:hover": { background: "rgb(0, 0, 0, 0.2)" },
                                },
                            }, popOutMenuZIndex: menuOptions.menuPopOutZIndex, type: "icon", color: "secondary", menuOptions: menuOptions.menuOptionsList, direction: "DOWN", offsetWidth: size }, { children: _jsx(MoreHorizIcon, { sx: { color: "white" } }) }))), _jsx(CardImage, __assign({}, props, { video: video, setVideo: setVideo, cardMediaHeight: cardMediaHeight, currentImageTextures: currentImageTextures, cardMediaClicked: cardMediaClicked, cardMedia360Load: cardMedia360Load || load3dModel, model3dAutoSpin: modelAutoSpin }))] })) })), _jsx(CardContent, __assign({ sx: __assign({ ":last-child": { paddingBottom: "16px" } }, (titleInEdit && { paddingTop: "2px !important", paddingBottom: "2px !important" })) }, { children: loading ? (_jsx(Grid, __assign({ container: true }, { children: _jsx(Grid, __assign({ item: true, xs: 12, container: true, justifyContent: "center" }, { children: _jsx(Skeleton, { variant: "text", sx: { width: "75%", fontSize: "2.1em" } }) })) }))) : (_jsxs(Grid, __assign({ container: true }, { children: [_jsx(Grid, __assign({ item: true, xs: 12, container: true, justifyContent: "center" }, { children: !titleInEdit ? (_jsx(Tooltip, __assign({ disableHoverListener: Boolean(title && title.length < titleCutOffPoint), placement: "top", arrow: true, title: title ? title : "" }, { children: _jsx(Typography
                                // gutterBottom
                                , __assign({ 
                                    // gutterBottom
                                    variant: "h5", fontStyle: {
                                        width: "250px",
                                        textAlign: "center",
                                    }, sx: __assign({}, (onTitleEdited && { cursor: "text" })), onClick: function () {
                                        if (onTitleEdited) {
                                            setEditedTitleValue(title);
                                            setTitleInEdit(true);
                                        }
                                    } }, { children: textCutOffAddElipsis(title, titleCutOffPoint) })) }))) : (_jsxs(Box, __assign({ component: "div", onBlur: function (event) {
                                    // if the blur was because of outside focus
                                    // currentTarget is the parent element, relatedTarget is the clicked element
                                    // this is so clicking the save edit button doesn't lose focus by default
                                    if (!event.currentTarget.contains(event.relatedTarget)) {
                                        window.setTimeout(function () {
                                            setTitleInEdit(false);
                                        }, 100);
                                    }
                                }, sx: {
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                } }, { children: [_jsx(VTTextField, { value: editedTitleValue, sx: { flex: 1 }, autoFocus: true, onChange: function (text) {
                                            setEditedTitleValue(text);
                                        }, onKeyDown: function (e) {
                                            var keyCodeForEnterKey = 13;
                                            var keyCodeForEscKey = 27;
                                            if (e.keyCode === keyCodeForEnterKey) {
                                                // grab value
                                                onTitleEdited && onTitleEdited(editedTitleValue);
                                                setTitleInEdit(false);
                                            }
                                            if (e.keyCode === keyCodeForEscKey) {
                                                setTitleInEdit(false);
                                            }
                                        } }), _jsx(IconButton, __assign({ sx: { marginLeft: "10px" }, onClick: function (e) {
                                            onTitleEdited && onTitleEdited(editedTitleValue);
                                            setTitleInEdit(false);
                                        } }, { children: _jsx(SaveAsRoundedIcon, {}) }))] }))) })), _jsx(Tooltip, __assign({ disableHoverListener: Boolean(text && text.length < textCutOffPoint), placement: "top", arrow: true, title: text ? text : "" }, { children: _jsx(Grid, __assign({ item: true, xs: 12, container: true, justifyContent: "center", sx: {
                                    maxHeight: "60px",
                                    overflowY: "auto",
                                    cursor: "ns-resize",
                                } }, { children: _jsx(Typography, __assign({ align: "center", variant: "body2" }, { children: text && textCutOffAddElipsis(text, textCutOffPoint) })) })) }))] }))) })), primaryActionButton && secondaryActionButton && (_jsx(CardActions, { children: _jsxs(Box, __assign({ component: "div", sx: { display: "flex", width: "100%" } }, { children: [_jsx(VTButton, __assign({ onClick: secondaryActionButton.onClick, sx: {
                                flex: 1,
                                marginRight: "4px",
                            }, color: "secondary" }, { children: secondaryActionButton.buttonText })), _jsx(VTButton, __assign({ onClick: primaryActionButton.onClick, sx: {
                                flex: 1,
                                marginLeft: "4px",
                            }, color: "primary" }, { children: primaryActionButton.buttonText }))] })) }))] }));
});
//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// VTCard use for web development to replace the material ui card
export default forwardRef(function (props, ref) {
    var onWholeCardClicked = props.onWholeCardClicked, sx = props.sx, primaryActionButton = props.primaryActionButton, secondaryActionButton = props.secondaryActionButton, mediaType = props.mediaType, loading = props.loading;
    mediaType = mediaType || {};
    if ((primaryActionButton && !secondaryActionButton) || (!primaryActionButton && secondaryActionButton)) {
        throw new Error("must pass in both primaryActionButton and secondaryActionButton props when either is used");
    }
    var rootRef = useRef(null);
    var vtMenuExposedMethodsRef = useRef(null);
    var _a = __read(useState(false), 2), cardHovered = _a[0], setCardHovered = _a[1];
    useImperativeHandle(ref, function () { return ({
        getRootNode: function () {
            return rootRef.current;
        },
        closePopUpOptionsMenu: function () {
            var _a;
            (_a = vtMenuExposedMethodsRef.current) === null || _a === void 0 ? void 0 : _a.closePopUpMenu();
        },
    }); });
    //--------------------------------------------------------------------------------------------
    // Card with included primary area
    if (!primaryActionButton &&
        mediaType &&
        !["ImageCardMedia360", "VideoCardMedia360", "ModelCardMedia"].includes(getMediaTypeAsString(mediaType))) {
        return (_jsx(StyledCard, __assign({ onMouseOver: function () {
                setCardHovered(true);
            }, onMouseLeave: function () {
                console.log("mouse leave");
                setCardHovered(false);
            }, ref: rootRef, elevation: 5, sx: sx }, { children: _jsx(CardActionArea, __assign({ disabled: loading, sx: {
                    "& .MuiCardActionArea-focusHighlight": {
                        background: "transparent",
                    },
                }, onClick: onWholeCardClicked }, { children: _jsx(CardContentComponentWrapper, __assign({ ref: vtMenuExposedMethodsRef }, props, { cardIsHovered: cardHovered })) })) })));
    }
    //-----------------------------------------------------------------------------------------
    // Card with onCLick method for the whole card
    else {
        return (_jsx(StyledCard, __assign({ onMouseOver: function () {
                setCardHovered(true);
            }, onMouseLeave: function () {
                console.log("mouse leave");
                setCardHovered(false);
            }, ref: rootRef, elevation: 5, sx: sx }, { children: _jsx(CardContentComponentWrapper, __assign({ ref: vtMenuExposedMethodsRef }, props, { cardIsHovered: cardHovered })) })));
    }
});
